import { useEffect, useRef } from "react";

// Web implementation. A sibling `.native.ts` provides a no-op stub so the
// orchestrator stays platform-agnostic — Metro picks `.native.ts` on native
// builds; Vite/webpack pick this file on web.
export function useBrowserHistorySync({
  stateTag,
  canGoBack,
  goBack,
}: {
  stateTag: string;
  canGoBack: boolean;
  goBack: (steps?: number) => void;
}) {
  const tagRef = useRef(stateTag);
  // Number of upcoming state changes that came from a history pop rather
  // than forward navigation — a multi-step rewind lands one change per step.
  const skipPushes = useRef(0);
  const pendingSteps = useRef(1);

  useEffect(() => {
    if (stateTag !== tagRef.current) {
      if (skipPushes.current > 0) {
        skipPushes.current -= 1;
      } else if (canGoBack) {
        // Push an entry so there is something for the browser's back button
        // (and gestures/keyboard shortcuts) to pop — popstate only fires when
        // the stack is non-empty. Storing `{ screen }` instead of `null` makes
        // the entry self-describing in DevTools and surfaces it as
        // `event.state` on popstate, which is useful for debugging and
        // future consistency checks.

        // TODO GRO-633: the UX is not great when the flow has multiple points
        // where back is not allowed. See
        // https://linear.app/medbillai/issue/GRO-633
        window.history.pushState({ screen: stateTag }, "");
      }
      tagRef.current = stateTag;
    }
  }, [stateTag, canGoBack]);

  useEffect(() => {
    const handlePopState = () => {
      if (canGoBack) {
        // popstate is our single choke point for navigation-driven goBack —
        // the forward-push effect skips one push per step so the stack stays
        // in sync. `history.go(-n)` fires a single popstate for n entries.
        const steps = pendingSteps.current;
        pendingSteps.current = 1;
        skipPushes.current = steps;
        goBack(steps);
        return;
      }
      // No back handler on this screen. Warn the user that pressing back
      // will leave the app and let them confirm or cancel. On cancel,
      // re-push so the stack stays non-empty and popstate keeps firing.
      const leave = window.confirm("Going back will leave the app. Continue?");
      if (!leave) {
        window.history.pushState({ screen: stateTag }, "");
      }
    };
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [stateTag, canGoBack, goBack]);

  // Routing the in-app back button through `history.back()` keeps popstate
  // the only place that calls `goBack()` for back navigation, so the
  // push/pop bookkeeping can't get out of sync. The orchestrator never
  // touches `window.history` directly.
  return {
    handleBack: (steps = 1) => {
      pendingSteps.current = steps;
      window.history.go(-steps);
    },
  };
}
